import { api } from '../../lib/api';
import type { PreviewSend } from '../../types/previewSend';
import type { PreviewSendBatch } from '../../types/previewSendBatch';
import type { PreviewLink } from '../../types/previewLink';

// Client-side API for Ad Preview Stage 3 sends. Authed AM-facing
// surfaces only (the public summary page uses plain fetch like the
// PreviewPage).

export type PreviewSendChannel = 'email' | 'sms';

// Mirrors the server orchestrator's OrchestratorResult (+ rejectedRows
// on the bulk path).
export type SendResult = {
  batchId: string;
  status: 'in_progress' | 'completed' | 'partial';
  totalSends: number;
  totalRecipients: number;
  pendingCount: number;
  failureCount: number;
  successCount: number;
  skippedChannels: PreviewSendChannel[];
  excluded: Array<{ contactId: string; reason: string }>;
  rejectedRows?: Array<{ rowId: string; reason: string }>;
};

export async function singleSendPreview(
  rolloutId: string,
  rowId: string,
  input: { channels: PreviewSendChannel[]; contactIds?: string[] },
): Promise<SendResult> {
  return api<SendResult>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/send-preview`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export async function bulkSendPreview(
  rolloutId: string,
  input: { rowIds: string[]; channels: PreviewSendChannel[] },
): Promise<SendResult> {
  return api<SendResult>(`/api/rollouts/${rolloutId}/preview-sends/bulk`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listSendsForRollout(
  rolloutId: string,
  channel?: PreviewSendChannel,
): Promise<PreviewSend[]> {
  const q = channel ? `?channel=${channel}` : '';
  const res = await api<{ sends: PreviewSend[] }>(
    `/api/rollouts/${rolloutId}/preview-sends${q}`,
  );
  return res.sends;
}

export async function listBatchesForRollout(
  rolloutId: string,
): Promise<PreviewSendBatch[]> {
  const res = await api<{ batches: PreviewSendBatch[] }>(
    `/api/rollouts/${rolloutId}/preview-send-batches`,
  );
  return res.batches;
}

export async function getPreviewSendBatch(id: string): Promise<PreviewSendBatch> {
  const res = await api<{ batch: PreviewSendBatch }>(
    `/api/preview-send-batches/${id}`,
  );
  return res.batch;
}

export async function listSendsForBatch(batchId: string): Promise<PreviewSend[]> {
  const res = await api<{ sends: PreviewSend[] }>(
    `/api/preview-send-batches/${batchId}/sends`,
  );
  return res.sends;
}

// Stage 6A v5 §3.3 — the raw pieces for the preview report. The client adapter
// (previewSendBatchToResultsData) assembles the SendResultsData from these.
export type PreviewSendReportResponse = {
  batch: PreviewSendBatch;
  sends: PreviewSend[];
  links: PreviewLink[];
  title: string;
  audienceDescription: string;
};

export async function getPreviewSendReport(
  batchId: string,
): Promise<PreviewSendReportResponse> {
  return api<PreviewSendReportResponse>(`/api/preview-sends/batch/${batchId}/report`);
}

// Retry one failed send -- creates a NEW PreviewSend in a new batch.
export async function retrySend(sendId: string): Promise<SendResult> {
  return api<SendResult>(`/api/preview-sends/${sendId}/retry`, {
    method: 'POST',
    body: '{}',
  });
}

// Retry every failed/bounced send in a batch -- new batch.
export async function retryFailedInBatch(batchId: string): Promise<SendResult> {
  return api<SendResult>(`/api/preview-send-batches/${batchId}/retry-failed`, {
    method: 'POST',
    body: '{}',
  });
}
